// SPDX-License-Identifier: AGPL-3.0-or-later

import {observable, runInAction} from 'mobx';

export interface WebglSupport {
	webgl2: boolean;
	// Unmasked GPU name when the browser exposes it; software rasterisers are recognised from it.
	renderer: string | null;
	hardwareConcurrency: number | null;
	deviceMemory: number | null;
}

type ProbeContext = Pick<WebGL2RenderingContext, 'RENDERER' | 'getExtension' | 'getParameter'>;

export function probeWebglSupport(doc: Document = document, nav: Navigator = navigator): WebglSupport {
	const device = {
		hardwareConcurrency: nav.hardwareConcurrency || null,
		deviceMemory: (nav as Navigator & {deviceMemory?: number}).deviceMemory || null,
	};
	let gl: ProbeContext | null = null;
	try {
		gl = doc.createElement('canvas').getContext('webgl2') as ProbeContext | null;
	} catch {
		gl = null;
	}
	if (!gl) return {webgl2: false, renderer: null, ...device};
	const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
	const renderer = gl.getParameter(debugInfo ? debugInfo.UNMASKED_RENDERER_WEBGL : gl.RENDERER);
	// Browsers cap live contexts; the probe must not hold one the room renderer will need.
	gl.getExtension('WEBGL_lose_context')?.loseContext();
	return {webgl2: true, renderer: typeof renderer === 'string' ? renderer : null, ...device};
}

// Chromium blocks WebGL for a site for about two minutes after two GPU process exits in a row (games reset the GPU in
// bursts), so a probe without webgl2 is repeated for a while instead of hiding the room for the whole session.
export const WEBGL_REPROBE_MS = 30_000;
export const WEBGL_REPROBE_LIMIT = 10;

const revision = observable.box(0);
let cached: WebglSupport | null = null;
let reprobesLeft = WEBGL_REPROBE_LIMIT;
let reprobeTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleReprobe(): void {
	if (reprobeTimer !== null || reprobesLeft <= 0) return;
	reprobeTimer = setTimeout(() => {
		reprobeTimer = null;
		reprobesLeft -= 1;
		const next = probeWebglSupport();
		if (!next.webgl2) {
			scheduleReprobe();
			return;
		}
		cached = next;
		runInAction(() => revision.set(revision.get() + 1));
	}, WEBGL_REPROBE_MS);
}

// Observable: an observer reading it re-renders once a repeated probe finds webgl2.
export function getWebglSupport(): WebglSupport {
	revision.get();
	if (cached === null) {
		cached = probeWebglSupport();
		if (!cached.webgl2) scheduleReprobe();
	}
	return cached;
}
